import { useState } from 'react';
import { Button, IconButton, Stack, TextField, Tooltip, MenuItem, Snackbar, Alert } from '@mui/material';
import { Add, Visibility, Delete } from '@mui/icons-material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { PageHeader } from '../../../components/PageHeader';
import { DataTable, type Column } from '../../../components/DataTable';
import { StatusBadge } from '../../../components/StatusBadge';
import { ConfirmDialog } from '../../../components/ConfirmDialog';
import { ProductionPlanFormDialog } from './ProductionPlanFormDialog';
import { ProductionPlanDetailDialog } from './ProductionPlanDetailDialog';
import { productionPlansApi, type ProductionPlan, type CreatePlanInput, type PlanStatus } from '../../../api/production.api';
import { extractApiError } from '../../../api/error';
import { useAuth } from '../../../hooks/useAuth';

const STATUSES: PlanStatus[] = ['draft', 'pending_approval', 'approved', 'active', 'closed', 'rejected', 'cancelled'];

export function ProductionPlanListPage() {
  const qc = useQueryClient();
  const { hasPermission } = useAuth();
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);
  const [status, setStatus] = useState('');
  const [formOpen, setFormOpen] = useState(false);
  const [detailId, setDetailId] = useState<number | null>(null);
  const [deleting, setDeleting] = useState<ProductionPlan | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['production-plans', page, limit, status],
    queryFn: () => productionPlansApi.list({ page, limit, status: status || undefined }),
  });
  const invalidate = () => qc.invalidateQueries({ queryKey: ['production-plans'] });

  const createMut = useMutation({
    mutationFn: (input: CreatePlanInput) => productionPlansApi.create(input),
    onSuccess: (p) => { setFormOpen(false); invalidate(); setDetailId(p.id); },
    onError: (e) => setFormError(extractApiError(e)),
  });
  const deleteMut = useMutation({
    mutationFn: (id: number) => productionPlansApi.remove(id),
    onSuccess: () => { setDeleting(null); invalidate(); },
    onError: (e) => { setToast(extractApiError(e)); setDeleting(null); },
  });

  const columns: Column<ProductionPlan>[] = [
    { key: 'planCode', label: 'Code', width: 120 },
    { key: 'title', label: 'Title' },
    { key: 'totalPlannedQty', label: 'Planned Qty', align: 'right' },
    { key: 'periodStart', label: 'From', render: (r) => r.periodStart ?? '—' },
    { key: 'status', label: 'Status', render: (r) => <StatusBadge status={r.status} /> },
  ];

  return (
    <>
      <PageHeader
        title="Production Plans"
        subtitle="Production planning, daily commitment and stock handover."
        action={hasPermission('production_plan.create') && <Button startIcon={<Add />} variant="contained" onClick={() => { setFormError(null); setFormOpen(true); }}>New Plan</Button>}
      />
      <Stack direction="row" mb={2}>
        <TextField select size="small" label="Status" value={status} onChange={(e) => { setStatus(e.target.value); setPage(1); }} sx={{ minWidth: 200 }}>
          <MenuItem value="">All</MenuItem>
          {STATUSES.map((s) => <MenuItem key={s} value={s}>{s.replace(/_/g, ' ')}</MenuItem>)}
        </TextField>
      </Stack>
      <DataTable
        columns={columns}
        rows={data?.data ?? []}
        loading={isLoading}
        page={page}
        limit={limit}
        total={data?.meta.total ?? 0}
        onPageChange={setPage}
        onLimitChange={(l) => { setLimit(l); setPage(1); }}
        rowKey={(r) => r.id}
        actions={(r) => (
          <>
            <Tooltip title="Open"><IconButton size="small" onClick={() => setDetailId(r.id)}><Visibility fontSize="small" /></IconButton></Tooltip>
            {hasPermission('production_plan.delete') && ['draft', 'rejected', 'cancelled'].includes(r.status) && (
              <Tooltip title="Delete"><IconButton size="small" color="error" onClick={() => setDeleting(r)}><Delete fontSize="small" /></IconButton></Tooltip>
            )}
          </>
        )}
      />

      <ProductionPlanFormDialog
        open={formOpen}
        error={formError}
        onClose={() => setFormOpen(false)}
        onSubmit={async (input) => { setFormError(null); await createMut.mutateAsync(input); }}
      />
      <ProductionPlanDetailDialog planId={detailId} onClose={() => setDetailId(null)} onChanged={invalidate} />
      <ConfirmDialog
        open={Boolean(deleting)}
        title="Delete plan"
        message={`Delete ${deleting?.planCode}?`}
        confirmLabel="Delete"
        onCancel={() => setDeleting(null)}
        onConfirm={() => deleting && deleteMut.mutate(deleting.id)}
      />
      <Snackbar open={Boolean(toast)} autoHideDuration={6000} onClose={() => setToast(null)}>
        <Alert severity="warning" onClose={() => setToast(null)}>{toast}</Alert>
      </Snackbar>
    </>
  );
}
